import React from 'react';

type RequiredInternalContextOptions = {
  name: string;
  isRequired?: true;
};

type OptionalInternalContextOptions<T> = {
  name: string;
  isRequired: false;
  defaultValue?: T;
};

export function createInternalContext<T>(
  options: RequiredInternalContextOptions,
): readonly [React.Provider<T | undefined>, () => T];
export function createInternalContext<T>(
  options: OptionalInternalContextOptions<T>,
): readonly [React.Provider<T | undefined>, () => T | undefined];
export function createInternalContext<T>(options: RequiredInternalContextOptions | OptionalInternalContextOptions<T>) {
  const { name, isRequired = true } = options;
  const defaultValue = 'defaultValue' in options ? options.defaultValue : undefined;
  const InternalContext = React.createContext<T | undefined>(defaultValue);
  InternalContext.displayName = `${name}Context`;

  function useInternalContext() {
    const value = React.useContext(InternalContext);

    if (value === undefined && isRequired) {
      throw new Error(`${name} must be used within ${name}Provider`);
    }

    return value;
  }

  return [InternalContext.Provider, useInternalContext] as const;
}
